import storage from '../../localStorage';

import { UTMKeys } from '../utmConfig';
import { extractUtmFromQuery } from './extractUtmFromQuery';
import referrerParser from './referrerParser';

const UTM_STORAGE_KEY = 'utm_data';
const INITIAL_UTM_STORAGE_KEY = 'utm_data_initial';
const DOCUMENT_REFERRER_KEY = 'referrer'; // The Document.referrer property returns the URI of the page that linked to this page.

export type UtmData = {
  [k in UTMKeys | 'referrer']?: string;
};

type AcceptedQueryKeys = UTMKeys | 'selectedStore';
type Query = {
  [k in AcceptedQueryKeys]?: string | string[];
};

function storeUtmQueryParamsToLocalStorage(query: Query) {
  const expiry = Date.now() + 24 * 3600 * 1000; // In 24 hours

  const utmData = extractUtmFromQuery(query) as UtmData;

  // Remove old utmData if it changes
  if (Object.keys(utmData).length) {
    storage.removeItem(UTM_STORAGE_KEY);
  }

  if (Object.keys(utmData).length) {
    storage.setItem(UTM_STORAGE_KEY, utmData, expiry);

    // Check if initial_utm_data already exists, if not set
    const initialUtmData = storage.getItem<UtmData | null>(
      INITIAL_UTM_STORAGE_KEY,
    );
    if (!initialUtmData || Object.keys(initialUtmData).length === 0) {
      storage.setItem(INITIAL_UTM_STORAGE_KEY, utmData);
    }
  }

  if (__isBrowser__) {
    const referrer = referrerParser(document.referrer);
    if (referrer) {
      storage.setItem(DOCUMENT_REFERRER_KEY, referrer, expiry);
    }
  }
}

function storeInitialUtmQueryParamsToLocalStorage(utmData: UtmData) {
  const expiry = Date.now() + 24 * 3600 * 1000; // In 24 hours

  // Only store if we don't already have initial data
  const existingInitialData = storage.getItem<UtmData | null>(
    INITIAL_UTM_STORAGE_KEY,
  );
  if (!existingInitialData || Object.keys(existingInitialData).length === 0) {
    // Filter out undefined values
    const filteredUtmData = Object.fromEntries(
      Object.entries(utmData).filter(([, value]) => value !== undefined),
    );

    if (Object.keys(filteredUtmData).length > 0) {
      storage.setItem(INITIAL_UTM_STORAGE_KEY, filteredUtmData, expiry);
    }
  }
}

function retrieveUtmQueryParamsFromLocalStorage(): UtmData {
  const utmData = storage.getItem<UtmData | null>(UTM_STORAGE_KEY) || {};
  const referrer = storage.getItem<string | null>(DOCUMENT_REFERRER_KEY);

  if (!utmData.utm_source && referrer) {
    // Fallback using referrer in case we don't have any UTM data. Not sure who
    // uses this, but we have 500k+ interests in Cloud with this utm_data so I
    // don't dare to remove it.
    return {
      utm_medium: utmData.utm_medium || DOCUMENT_REFERRER_KEY,
      utm_source: referrer,
    };
  }

  if (referrer) {
    // Inside an <iframe>, the Document.referrer will initially be set
    // to the same value as the href of the parent window's Window.location.
    utmData.referrer = referrer;
  }

  return utmData;
}

function retrieveInitialUtmQueryParamsFromLocalStorage(): UtmData {
  const initialUtmData =
    storage.getItem<UtmData | null>(INITIAL_UTM_STORAGE_KEY) || {};
  const referrer = storage.getItem<string | null>(DOCUMENT_REFERRER_KEY);

  if (!initialUtmData.utm_source && referrer) {
    // Maintaining the same fallback as above (:63) for initial utm data
    return {
      utm_medium: initialUtmData.utm_medium || DOCUMENT_REFERRER_KEY,
      utm_source: referrer,
    };
  }

  if (referrer) {
    // Inside an <iframe>, the Document.referrer will initially be set
    // to the same value as the href of the parent window's Window.location.
    initialUtmData.referrer = referrer;
  }

  return initialUtmData;
}

function clearUtmQueryParamsFromLocalStorage() {
  storage.removeItem(UTM_STORAGE_KEY);
  storage.removeItem(DOCUMENT_REFERRER_KEY);
}

const UtmLocalStorage = {
  store: storeUtmQueryParamsToLocalStorage,
  storeInitial: storeInitialUtmQueryParamsToLocalStorage,
  retrieve: retrieveUtmQueryParamsFromLocalStorage,
  retrieveInitial: retrieveInitialUtmQueryParamsFromLocalStorage,
  clear: clearUtmQueryParamsFromLocalStorage,
};

export default UtmLocalStorage;
